import { Header } from '../../components/feature/Header';
import { Footer } from '../../components/feature/Footer';
import { SEO } from '../../utils/seo';

export function ApplicationsPage() {
  const applications = [
    {
      id: 'messen-events',
      title: 'Messen & Events',
      description: 'Professionelle Präsentation auf Messen, Kongressen und Veranstaltungen',
      features: [
        'Maximale Sichtbarkeit am Messestand',
        'Einfacher Transport und Aufbau',
        'Wetterfeste Materialien für Outdoor-Events',
        'Individuelle Gestaltung nach Corporate Design'
      ],
      image: 'https://static.readdy.ai/image/07f91bd9f2ad3b84149b39a4e0627797/b40ff369cf3b7112cc774bada112d6ac.jpeg',
      icon: 'ri-presentation-line'
    },
    {
      id: 'einzelhandel',
      title: 'Einzelhandel & Filialen',
      description: 'Kundenmagnet für Geschäfte, Restaurants und Dienstleister',
      features: [
        'Aufmerksamkeitsstarke Außenwerbung',
        'Kostengünstige Werbemaßnahme',
        'Flexible Positionierung je nach Bedarf',
        'Langlebige Investition für dauerhaften Einsatz'
      ],
      image: 'https://static.readdy.ai/image/07f91bd9f2ad3b84149b39a4e0627797/3861c2d70debfde3ca673d77e74d903f.jpeg',
      icon: 'ri-store-2-line'
    },
    {
      id: 'firmengebaeude',
      title: 'Firmengebäude',
      description: 'Repräsentative Kennzeichnung von Unternehmensstandorten',
      features: [
        'Professionelle Außendarstellung',
        'Einfache Orientierung für Besucher',
        'Repräsentation der Markenidentität',
        'Verschiedene Befestigungsmöglichkeiten'
      ],
      image: 'https://static.readdy.ai/image/07f91bd9f2ad3b84149b39a4e0627797/580d1f434f10658b82867d6c729af20e.jpeg',
      icon: 'ri-building-line'
    },
    {
      id: 'outdoor-werbung',
      title: 'Outdoor-Werbung',
      description: 'Effektive Außenwerbung für maximale Reichweite',
      features: [
        'Hohe Reichweite und Sichtbarkeit',
        'Witterungsbeständige Materialien',
        'Flexible Standortwahl möglich',
        'Kosteneffiziente Werbemaßnahme'
      ],
      image: 'https://static.readdy.ai/image/07f91bd9f2ad3b84149b39a4e0627797/5358b4b438944b2110043d68633d4dca.jpeg',
      icon: 'ri-road-map-line'
    },
    {
      id: 'promotion',
      title: 'Promotion-Aktionen',
      description: 'Zeitlich begrenzte Werbeaktionen und Sonderangebote',
      features: [
        'Schnelle Umsetzung kurzfristiger Aktionen',
        'Mobile und flexible Einsatzmöglichkeiten',
        'Hohe Aufmerksamkeit für Sonderangebote',
        'Kostengünstige Kurzzeitwerbung'
      ],
      image: 'https://static.readdy.ai/image/07f91bd9f2ad3b84149b39a4e0627797/f17be1ce774d4047cb183aa46582b2a6.jpeg',
      icon: 'ri-megaphone-line'
    },
    {
      id: 'sport-events',
      title: 'Sport & Freizeit',
      description: 'Sponsoring und Kennzeichnung bei Sportveranstaltungen',
      features: [
        'Windstabile Konstruktion für Outdoor-Events',
        'Sichtbarkeit auch bei Bewegung',
        'Wetterfeste UV-beständige Drucke',
        'Verschiedene Größen für jeden Einsatzbereich'
      ],
      image: 'https://static.readdy.ai/image/07f91bd9f2ad3b84149b39a4e0627797/23b4573c2d5c0c749ac3a191cfa45d1c.jpeg',
      icon: 'ri-football-line'
    }
  ];

  const schema = {
    '@type': 'WebPage',
    name: 'Einsatzbereiche - Vielfältige Anwendungsbereiche für Promotion-Fahnen',
    description: 'Entdecken Sie die zahlreichen Möglichkeiten: Messen & Events, Einzelhandel, Firmengebäude, Outdoor-Werbung, Promotion-Aktionen und Sport & Freizeit.',
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: applications.map((app, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Service',
          name: app.title,
          description: app.description
        }
      }))
    }
  };

  return (
    <>
      <SEO
        title="Einsatzbereiche - Anwendungen für Promotion-Fahnen | Horizon Signage Deutschland"
        description="Entdecken Sie die zahlreichen Möglichkeiten für Promotion-Fahnen: Messen & Events, Einzelhandel & Filialen, Firmengebäude, Outdoor-Werbung, Promotion-Aktionen und Sport & Freizeit. Vielfältige Anwendungsbereiche für maximale Sichtbarkeit."
        keywords="Einsatzbereiche, Anwendungen, Messen, Events, Einzelhandel, Outdoor-Werbung, Promotion-Aktionen, Deutschland"
        canonical={`${import.meta.env.VITE_SITE_URL || 'https://example.com'}/einsatzbereiche`}
        schema={schema}
      />
      <div className="min-h-screen">
        <Header />
        
        <main className="pt-20">
          {/* Hero Section */}
          <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
            <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
              <span className="inline-block px-4 py-2 bg-zinc-800 text-white rounded-full text-sm font-semibold uppercase tracking-wider mb-6">
                Einsatzbereiche
              </span>
              <h1 className="text-5xl lg:text-6xl font-bold text-zinc-800 leading-tight mb-6">
                <span className="block">Vielfältige</span>
                <span className="block text-gray-600">Anwendungsbereiche</span>
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed">
                Entdecken Sie die zahlreichen Möglichkeiten, wie unsere hochwertigen 
                Promotion-Fahnen Ihr Unternehmen optimal präsentieren können.
              </p>
            </div>
          </section>

          {/* Applications Grid */}
          <section className="py-20 bg-white">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
              <div className="space-y-20">
                {applications.map((app, index) => (
                  <div
                    key={app.id}
                    className={`grid grid-cols-1 lg:grid-cols-2 gap-16 items-center ${
                      index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''
                    }`}
                  >
                    {/* Image */}
                    <div className={`${index % 2 === 1 ? 'lg:col-start-2' : ''}`}>
                      <div className="relative">
                        <img
                          src={app.image}
                          alt={app.title}
                          className="w-full h-80 object-cover rounded-2xl shadow-xl"
                        />
                        <div className="absolute -bottom-6 -right-6 w-16 h-16 bg-zinc-800 rounded-2xl flex items-center justify-center shadow-xl">
                          <i className={`${app.icon} text-2xl text-white`}></i>
                        </div>
                      </div>
                    </div>

                    {/* Content */}
                    <div className={`${index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}`}>
                      <div className="mb-6">
                        <span className="inline-block px-3 py-1 bg-gray-100 text-gray-700 text-sm font-semibold rounded-full uppercase tracking-wider mb-4">
                          Einsatzbereich {index + 1}
                        </span>
                        <h2 className="text-4xl font-bold text-zinc-800 mb-4">
                          {app.title}
                        </h2>
                        <p className="text-xl text-gray-600 leading-relaxed">
                          {app.description}
                        </p>
                      </div>

                      {/* Features */}
                      <div className="mb-8">
                        <h3 className="text-lg font-semibold text-zinc-800 mb-4">
                          Ihre Vorteile:
                        </h3>
                        <ul className="space-y-3">
                          {app.features.map((feature, featureIndex) => (
                            <li key={featureIndex} className="flex items-start">
                              <div className="w-6 h-6 flex items-center justify-center bg-green-100 rounded-full mr-3 mt-0.5 flex-shrink-0">
                                <i className="ri-check-line text-green-600 text-sm"></i>
                              </div>
                              <span className="text-gray-700">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* CTA */}
                      <div className="flex flex-col sm:flex-row gap-4">
                        <a
                          href="/produkte"
                          className="inline-flex items-center justify-center px-6 py-3 bg-zinc-800 text-white font-semibold rounded-lg hover:bg-zinc-900 transition-colors whitespace-nowrap"
                        >
                          Passende Produkte
                          <i className="ri-arrow-right-line ml-2"></i>
                        </a>
                        <a
                          href="/kontakt"
                          className="inline-flex items-center justify-center px-6 py-3 border-2 border-zinc-800 text-zinc-800 font-semibold rounded-lg hover:bg-zinc-800 hover:text-white transition-colors whitespace-nowrap"
                        >
                          Beratung anfragen
                          <i className="ri-phone-line ml-2"></i>
                        </a>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Industries Section */}
          <section className="py-20 bg-gray-50">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold text-zinc-800 mb-6">
                  Unsere Branchen-Expertise
                </h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  Wir haben bereits für Unternehmen aus verschiedensten Branchen 
                  erfolgreich Promotion-Fahnen realisiert.
                </p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
                {[
                  { name: 'Automobil', icon: 'ri-car-line' },
                  { name: 'Gastronomie', icon: 'ri-restaurant-line' },
                  { name: 'Immobilien', icon: 'ri-home-line' },
                  { name: 'Technologie', icon: 'ri-computer-line' },
                  { name: 'Gesundheit', icon: 'ri-heart-pulse-line' },
                  { name: 'Bildung', icon: 'ri-graduation-cap-line' }
                ].map((industry, index) => (
                  <div
                    key={industry.name}
                    className="text-center group hover:transform hover:scale-105 transition-all duration-300"
                  >
                    <div className="w-16 h-16 mx-auto mb-4 bg-white rounded-2xl shadow-lg flex items-center justify-center group-hover:bg-zinc-800 group-hover:shadow-xl transition-all duration-300">
                      <i className={`${industry.icon} text-2xl text-zinc-800 group-hover:text-white transition-colors duration-300`}></i>
                    </div>
                    <h3 className="text-sm font-semibold text-zinc-800 group-hover:text-zinc-900 transition-colors">
                      {industry.name}
                    </h3>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-20 bg-zinc-800">
            <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
              <h2 className="text-4xl font-bold text-white mb-6">
                Bereit für Ihr nächstes Projekt?
              </h2>
              <p className="text-xl text-gray-300 mb-10">
                Lassen Sie uns gemeinsam die perfekte Lösung für Ihren 
                Anwendungsbereich entwickeln.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="/kontakt"
                  className="inline-flex items-center justify-center px-8 py-4 bg-white text-zinc-800 font-semibold rounded-lg hover:bg-gray-100 transition-colors whitespace-nowrap"
                >
                  Projekt besprechen
                  <i className="ri-arrow-right-line ml-2"></i>
                </a>
                <a
                  href="/produkte"
                  className="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-zinc-800 transition-colors whitespace-nowrap"
                >
                  Produktkatalog
                  <i className="ri-external-link-line ml-2"></i>
                </a>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}
